<template>
  <!--
    One step of the guided tour, told by Fency like the browser extension's
    dialogs (content-scripts/protection/utils/popup.ts): Fency's head beside a
    chat bubble, typing dots first, then the message typed out while Fency
    talks. Mounted by use-dashboard-tour.ts in the driver.js popover body; the
    title lives in the popover header. Under prefers-reduced-motion the text
    is shown at once.
  -->
  <div class="tf-tour-chat">
    <FencyCharacter
      pose="head"
      :height="44"
      :mood="fencyMood"
      :entrance="false"
      class="flex-none"
    />

    <!-- The full text, invisible, reserves the bubble's final size so the
         popover doesn't grow (and get repositioned) while it types out.
         Screen readers get the whole message at once. A click skips ahead. -->
    <div class="tf-tour-bubble" @click="finishTyping">
      <span class="invisible col-start-1 row-start-1" aria-hidden="true">
        {{ body }}
      </span>
      <span
        v-if="!dots"
        class="col-start-1 row-start-1"
        aria-hidden="true"
      >
        {{ typed }}
      </span>
      <span
        v-else
        class="tf-tour-typing col-start-1 row-start-1"
        aria-hidden="true"
      >
        <span /><span /><span />
      </span>
      <span class="sr-only">{{ body }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, type Ref } from "vue";
import FencyCharacter from "../Fency/FencyCharacter.vue";
import { engineMood } from "../Learning/copilot-moods";

const props = defineProps<{
  body: string;
  /** A CopilotMood (copilot-moods.ts); unknown values read informative. */
  mood?: string;
  /** True while the pointer is on the tour's primary button. */
  cheering: Ref<boolean>;
}>();

const typed = ref("");
const dots = ref(false);
const talking = ref(false);
let timer = 0;

// Fency talks while typing, cheers up when the user reaches for "Next"
// (as on the extension's primary button), otherwise shows the step's mood.
const fencyMood = computed(() => {
  if (talking.value) return "talking";
  if (props.cheering.value) return "happy";
  return engineMood(props.mood);
});

const finishTyping = () => {
  window.clearTimeout(timer);
  dots.value = false;
  typed.value = props.body;
  talking.value = false;
};

// The extension's rhythm: 0.8-1.2s of typing dots, then 12-20ms a character.
const typeFrom = (length: number) => {
  if (length > props.body.length) return finishTyping();
  typed.value = props.body.slice(0, length);
  timer = window.setTimeout(() => typeFrom(length + 1), 12 + Math.random() * 8);
};

onMounted(() => {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    finishTyping();
    return;
  }
  talking.value = true;
  dots.value = true;
  timer = window.setTimeout(() => {
    dots.value = false;
    typeFrom(1);
  }, 800 + Math.random() * 400);
});

onUnmounted(() => window.clearTimeout(timer));
</script>
